import { LoadingOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import debounce from 'lodash/debounce';
import React, { useEffect, useState } from 'react';

const GOOGLE_API_KEY = 'AIzaSyDJZXbru9k1UWfwFZ0vvCCPxlTqwGnPP5s';
const GOOGLE_MAPS_SCRIPT_URL = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&callback=initGooglePlaces`;

interface AddressOption {
    value: string;
    label: string;
    address: string;
    placeId: string;
}

interface AddressSearchProps {
    onSelect: (addressData: AddressData) => void;
    countryRestriction?: string;
    style?: React.CSSProperties;
    value?: string | null;
}

export interface AddressData {
    street: string | null;
    street2: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    postalCode: string | null;
}

const AddressSearch: React.FC<AddressSearchProps> = React.forwardRef<any, AddressSearchProps>(
    ({ onSelect, countryRestriction, style, value }, _ref) => {
        const [options, setOptions] = useState<AddressOption[]>([]);
        const [loading, setLoading] = useState(false);
        const [autocompleteService, setAutocompleteService] = useState<any>(null);
        const [placesService, setPlacesService] = useState<any>(null);

        useEffect(() => {
            if (typeof window !== 'undefined') {
                if (!document.querySelector(`script[src="${GOOGLE_MAPS_SCRIPT_URL}"]`)) {
                    window.initGooglePlaces = () => {
                        const autocomplete = new window.google.maps.places.AutocompleteService();
                        const places = new window.google.maps.places.PlacesService(document.createElement('div'));

                        setAutocompleteService(autocomplete);
                        setPlacesService(places);
                    };

                    const script = document.createElement('script');
                    script.src = GOOGLE_MAPS_SCRIPT_URL;
                    script.async = true;
                    script.defer = true;
                    document.head.appendChild(script);

                    return () => {
                        document.head.removeChild(script);
                        delete window.initGooglePlaces;
                    };
                }
            }
        }, []);

        const fetchPlaceDetails = async (placeId: string): Promise<any> => {
            return new Promise((resolve, reject) => {
                if (!placesService) {
                    reject(new Error('Places service not initialized'));
                    return;
                }

                placesService.getDetails(
                    {
                        placeId,
                        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
                    },
                    (result: any, status: any) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                            resolve(result);
                        } else {
                            reject(new Error(`Place details request failed: ${status}`));
                        }
                    },
                );
            });
        };

        const searchAddresses = async (value: string) => {
            if (!value || value.length < 3 || !autocompleteService) {
                setOptions([]);
                return;
            }

            setLoading(true);

            try {
                const request: {
                    input: string;
                    componentRestrictions?: { country: string };
                    types: string[];
                } = {
                    input: value,
                    types: ['address'],
                };

                if (countryRestriction) {
                    request.componentRestrictions = { country: countryRestriction };
                }

                const response = await new Promise((resolve, reject) => {
                    autocompleteService.getPlacePredictions(request, (results: any, status: any) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                            resolve(results);
                        } else {
                            reject(new Error(`Autocomplete request failed: ${status}`));
                        }
                    });
                });

                const predictions = (response as any[]).map((result: any) => ({
                    value: result.place_id,
                    label: result.description,
                    address: result.description,
                    placeId: result.place_id,
                }));

                setOptions(predictions);
            } catch (error) {
                console.error('Error fetching addresses:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        const extractAddressComponents = (details: any): AddressData => {
            const data: AddressData = {
                street: null,
                street2: null,
                city: null,
                region: null,
                country: null,
                postalCode: null,
            };

            details.address_components.forEach((component: any) => {
                const types = component.types;
                if (types.includes('street_number') || types.includes('route')) {
                    data.street = data.street ? `${data.street} ${component.long_name}` : component.long_name;
                }
                if (types.includes('locality')) data.city = component.long_name;
                if (types.includes('administrative_area_level_1')) data.region = component.long_name;
                if (types.includes('country')) data.country = component.short_name.toLowerCase();
                if (types.includes('postal_code')) data.postalCode = component.long_name;
            });

            return data;
        };

        const handleSelect = async (value: string, option: any) => {
            if (!option) return;

            try {
                const details = await fetchPlaceDetails(option.placeId);
                const extractedData = extractAddressComponents(details);
                onSelect(extractedData);
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        };

        const debouncedSearch = debounce(searchAddresses, 300);

        return (
            <Select
                showSearch
                placeholder="Search for an address"
                defaultActiveFirstOption={false}
                filterOption={false}
                onSearch={debouncedSearch}
                onChange={handleSelect}
                notFoundContent={loading ? <LoadingOutlined /> : null}
                options={options}
                style={{ width: '100%', ...style }}
                value={value}
            />
        );
    },
);

export default AddressSearch;
